import React, { useEffect, useState } from "react";
import EnrollmentService from "../../../_services/EnrollmentService";
import { openStudentGradeReport } from "../../../_services/reports.service";

interface EnrollmentDrawerProps {
  enrollmentId: number | null;
  photoPath?: string | null;
  onClose: () => void;
}

const DRAWER_WIDTH = 520;

/* Same palette helper as SearchResults / EnrollmentPopover so the drawer's
   status pill matches the row that opened it. */
const statusPalette = (raw: string): { bg: string; fg: string; border: string } => {
  const s = String(raw || "").toLowerCase();
  if (!s) return { bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" };
  if (s.includes("dropout") || s.includes("inactive") || s.includes("cancel")) {
    return { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" };
  }
  if (s.includes("hold") || s.includes("pending") || s.includes("draft")) {
    return { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" };
  }
  if (s.includes("regular") || s.includes("active") || s.includes("enroll")) {
    return { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" };
  }
  return { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" };
};

const EnrollmentDrawer: React.FC<EnrollmentDrawerProps> = ({
  enrollmentId,
  photoPath,
  onClose,
}) => {
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  useEffect(() => {
    if (!enrollmentId) {
      setDetails(null);
      return;
    }
    setLoading(true);
    EnrollmentService.getDetails(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) setDetails(res.data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  const isOpen = enrollmentId != null;

  /* Reusable label/value row with hover affordance. Two-column layout so
     long values wrap cleanly rather than colliding with the label. */
  const Row: React.FC<{ label: string; value: any }> = ({ label, value }) => {
    const isEmpty = value == null || value === "" || value === "--";
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "120px 1fr",
          gap: 16,
          padding: "8px 0",
          borderBottom: "1px solid var(--border-subtle)",
          fontSize: "0.82rem",
          alignItems: "baseline",
        }}
      >
        <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>{label}</span>
        <span
          style={{
            fontWeight: isEmpty ? 400 : 600,
            color: isEmpty ? "#94a3b8" : "#0f172a",
            textAlign: "right",
            wordBreak: "break-word",
          }}
        >
          {isEmpty ? "—" : value}
        </span>
      </div>
    );
  };

  /* Section card: title band + body. Used to group related rows so the
     drawer reads as a stack of focused panels instead of one long list. */
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: 10,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 14px",
          background: "var(--surface-page)",
          borderBottom: "1px solid var(--border-default)",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "var(--text-body)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </div>
      <div style={{ padding: "4px 14px 8px" }}>{children}</div>
    </div>
  );

  const pal = details?.enrollmentStatusName
    ? statusPalette(details.enrollmentStatusName)
    : null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(var(--surface-inverse-rgb), 0.42)",
            backdropFilter: "blur(2px)",
            zIndex: 1040,
          }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: DRAWER_WIDTH,
          maxWidth: "92vw",
          height: "100vh",
          background: "var(--surface-page)",
          boxShadow: "-12px 0 32px rgba(15,23,42,0.18)",
          zIndex: 1050,
          transform: isOpen ? "translateX(0)" : `translateX(${DRAWER_WIDTH}px)`,
          transition: "transform 0.25s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--surface-card)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "var(--status-info-bg)",
                color: "var(--brand-primary-hover)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
              }}
            >
              <i className="fa fa-id-card-o" />
            </span>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text-strong)" }}>
              Enrollment Quick View
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              color: "var(--text-muted)",
              cursor: "pointer",
              fontSize: 14,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <i className="fa fa-times" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 20 }} />
              <div style={{ marginTop: 10, fontSize: "0.85rem" }}>Loading…</div>
            </div>
          ) : details ? (
            <>
              {/* Identity card */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: 14,
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 10,
                  marginBottom: 12,
                }}
              >
                {photoPath ? (
                  <img
                    src={photoPath}
                    alt=""
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "1px solid var(--border-default)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: "var(--surface-sunken)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      color: "var(--text-muted)",
                    }}
                  >
                    <i className="fa fa-user" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "var(--text-strong)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {details.studentName}
                  </div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      color: "var(--text-muted)",
                      marginTop: 2,
                    }}
                  >
                    {details.studentCode}
                    {details.studentCode2 ? ` • ${details.studentCode2}` : ""}
                  </div>
                  {details.enrollmentStatusName && pal && (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 10px",
                        marginTop: 6,
                        borderRadius: 12,
                        background: pal.bg,
                        color: pal.fg,
                        border: `1px solid ${pal.border}`,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {details.enrollmentStatusName}
                    </span>
                  )}
                </div>
              </div>

              <Section title="Academic Info">
                <Row label="Enquiry ID" value={details.enquiryId} />
                <Row label="School / Centre" value={details.schoolName} />
                <Row label="Batch" value={details.batchName} />
                <Row label="Year" value={details.batchYearName} />
                <Row label="Entry Year" value={details.entryYearName} />
                <Row label="Entry Term" value={details.entryTermName} />
                <Row label="Current Term" value={details.currentTermName} />
                <Row label="Join Date" value={details.joiningDate} />
              </Section>

              <Section title={details.isProgramBased ? "Program" : "Course"}>
                {details.isProgramBased ? (
                  <Row label="Program" value={details.programName} />
                ) : (
                  <Row label="Course" value={details.courseName} />
                )}
                <Row label="Plan" value={details.installmentTemplateName} />
                <Row label="Faculty Advisor" value={details.facultyAdvisorNames} />
              </Section>

              <Section title="User Accounts">
                <Row label="Student Username" value={details.studentUserName} />
                <Row label="Parent Username" value={details.parentUserName} />
                <Row label="Counsellor" value={details.counsellor} />
              </Section>

              {details.tags && details.tags.length > 0 && (
                <Section title="Tags">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingTop: 6, paddingBottom: 4 }}>
                    {details.tags.map((tag: any, idx: number) => (
                      <span
                        key={idx}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "3px 10px",
                          borderRadius: 12,
                          background: "var(--status-info-bg)",
                          color: "var(--status-info-text)",
                          border: "1px solid #bfdbfe",
                          fontSize: "0.72rem",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{tag.typeName}:</span>
                        <span>{tag.tagName}</span>
                      </span>
                    ))}
                  </div>
                </Section>
              )}
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: 60,
                color: "var(--text-muted)",
                background: "var(--surface-card)",
                border: "1px solid var(--border-default)",
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 28, color: "var(--text-faint)", marginBottom: 10 }}>
                <i className="fa fa-folder-open-o" />
              </div>
              <div style={{ fontWeight: 600, color: "var(--text-strong)" }}>No data</div>
            </div>
          )}
        </div>

        {/* Footer */}
        {details && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: "1px solid var(--border-default)",
              background: "var(--surface-card)",
            }}
          >
            {reportError && (
              <div
                style={{
                  fontSize: "0.78rem",
                  color: "var(--status-danger)",
                  background: "var(--status-danger-bg)",
                  border: "1px solid var(--status-danger-border)",
                  borderRadius: 6,
                  padding: "6px 10px",
                  marginBottom: 10,
                }}
              >
                {reportError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() =>
                  window.open(`/enrollment/view/${enrollmentId}`, "_blank")
                }
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "9px 14px",
                  background:
                    "var(--gradient-brand)",
                  color: "var(--text-on-brand)",
                  border: "none",
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                  transition: "opacity .12s, box-shadow .12s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.92";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(79,70,229,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.08)";
                }}
              >
                <i className="fa fa-external-link" />
                Open Full View
              </button>
              {details.enquiryId && details.batchId && (
                <button
                  type="button"
                  disabled={reportLoading}
                  onClick={async () => {
                    setReportLoading(true);
                    setReportError("");
                    try {
                      const result = await openStudentGradeReport(
                        details.enquiryId,
                        details.batchId,
                      );
                      if (!result.success) setReportError(result.message);
                    } catch (err: any) {
                      setReportError(err.message || "Failed to generate report");
                    } finally {
                      setReportLoading(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "9px 14px",
                    background: "var(--surface-card)",
                    color: "var(--text-strong)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: reportLoading ? "not-allowed" : "pointer",
                    opacity: reportLoading ? 0.7 : 1,
                    transition: "background-color .12s",
                  }}
                  onMouseEnter={(e) => {
                    if (!reportLoading) e.currentTarget.style.background = "var(--surface-page)";
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
                >
                  {reportLoading ? (
                    <>
                      <i className="fa fa-spinner fa-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <i className="fa fa-file-text-o" />
                      Grade Report
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EnrollmentDrawer;
